/**
 * Filterable Select Controller
 * 
 * A reusable Stimulus controller for filtering options in a select element
 * based on a search input.
 * 
 * @description Generic controller for client-side filtering of select options.
 * Each option should have a data-search-text attribute for searchable text.
 * 
 * @example Basic usage:
 *   <div data-controller="filterable-select">
 *     <input type="text" 
 *            data-filterable-select-target="search"
 *            data-action="input->filterable-select#filter"
 *            placeholder="Search...">
 *     <select multiple data-filterable-select-target="select">
 *       <option data-search-text="john doe john@example.com">John Doe</option>
 *       <option data-search-text="jane smith jane@example.com">Jane Smith</option>
 *     </select>
 *   </div>
 */

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "select"]
  
  connect() {
    // Store original options for filtering
    if (this.hasSelectTarget) {
      this.originalOptions = Array.from(this.selectTarget.options).map(option => ({
        value: option.value,
        text: option.textContent,
        searchText: option.dataset.searchText || option.textContent.toLowerCase(),
        selected: option.selected
      }))
    }
  }
  
  /**
   * Filters the select options based on search input
   */
  filter(event) {
    if (!this.hasSelectTarget || !this.originalOptions) return
    
    const query = event.target.value.toLowerCase().trim()
    
    // Store current selections before filtering
    const selectedValues = Array.from(this.selectTarget.selectedOptions).map(o => o.value)
    
    // Clear and rebuild options
    this.selectTarget.innerHTML = ''
    
    this.originalOptions.forEach(optionData => {
      // Show all if query is empty, otherwise filter by search text
      if (query === '' || optionData.searchText.includes(query)) {
        const option = document.createElement('option')
        option.value = optionData.value
        option.textContent = optionData.text
        option.dataset.searchText = optionData.searchText
        
        // Restore selection state
        if (selectedValues.includes(optionData.value)) {
          option.selected = true
        }
        
        this.selectTarget.appendChild(option)
      }
    })
    
    // Dispatch event for other controllers
    this.dispatch('filtered', { 
      detail: { 
        query, 
        visibleCount: this.selectTarget.options.length,
        totalCount: this.originalOptions.length
      }
    })
  }
  
  /**
   * Clears the search and shows all options
   */
  clear() {
    if (this.hasSearchTarget) {
      this.searchTarget.value = ''
      this.filter({ target: this.searchTarget })
    }
  }
}